"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createLiveReact = void 0;
var tslib_1 = require("tslib");
var React = (0, tslib_1.__importStar)(require("react"));
var createLive_1 = require("./createLive");
/**
 * 创建 {@link LiveReact} 实例，并提供 `React` 技术栈相关工具链。
 */
function createLiveReact(options) {
    var LiveContext = React.createContext(null);
    var live = (0, createLive_1.createLive)(options);
    function LiveProvider(props) {
        return React.createElement(LiveContext.Provider, { value: props.live || live }, props.children);
    }
    function useLiveInstance() {
        var context = React.useContext(LiveContext);
        if (!context)
            throw new Error('LiveProvider never found.');
        return context;
    }
    function unsafe__useLiveInstance() {
        return useLiveInstance();
    }
    function useRTCInstance() {
        var live = useLiveInstance();
        return live.$RTC;
    }
    function unsafe__useRTCInstance() {
        return useRTCInstance();
    }
    function useConnect() {
        var _this = this;
        var live = useLiveInstance();
        return function (options) { return (0, tslib_1.__awaiter)(_this, void 0, void 0, function () { return (0, tslib_1.__generator)(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, live.connect(options)];
                case 1: return [2 /*return*/, _a.sent()];
            }
        }); }); };
    }
    function useCurrentWebSocketUrl() {
        var live = useLiveInstance();
        var _a = React.useState(undefined), state = _a[0], setState = _a[1];
        React.useEffect(function () {
            var onWSBeforeConnect = function () { return setState(live.url); };
            live.on('ws.beforeConnect', onWSBeforeConnect);
            return function () { return live.off('ws.beforeConnect', onWSBeforeConnect); };
        }, [live]);
        return state;
    }
    function useKeyframeUpdateCallback(name, callback, deps) {
        var live = useLiveInstance();
        var dependencyList = deps ? [live, name].concat(deps) : [live, name];
        var dispose = function () { return live.keyframes.off(name, callback); };
        React.useEffect(function () {
            live.keyframes.on(name, callback);
            return dispose;
        }, dependencyList);
        return dispose;
    }
    function useLiveAction() {
        var live = useLiveInstance();
        var toggleMicro = React.useCallback(function (microStatus) { return live.toggleMicro(microStatus); }, [live]);
        var broadcast = React.useCallback(function (data, toUserIds, timeout) {
            if (timeout === void 0) { timeout = 0; }
            return live.broadcast(data, toUserIds, timeout);
        }, [live]);
        var exit = React.useCallback(function () { return live.exit(); }, [live]);
        var kick = React.useCallback(function (toUserIds) { return live.kick(toUserIds); }, [live]);
        var forbidMicro = React.useCallback(function (micro, toUserIds) { return live.forbidMicro(micro, toUserIds); }, [live]);
        var forbidSync = React.useCallback(function (sync, toUserIds) { return live.forbidSync(sync, toUserIds); }, [live]);
        var getFramesByKey = React.useCallback(function (key) { return live.getFramesByKey(key); }, [live]);
        var sendKeyframe = React.useCallback(function (key, frame) { return live.sendKeyframe(key, frame); }, [live]);
        var setSelfInfo = React.useCallback(function (userInfo) { return live.setSelfInfo(userInfo); }, [live]);
        var updateStatus = React.useCallback(function (evtType, atnType, status, toUserIds) { return live.updateStatus(evtType, atnType, status, toUserIds); }, [live]);
        return {
            broadcast: broadcast,
            exit: exit,
            kick: kick,
            toggleMicro: toggleMicro,
            forbidMicro: forbidMicro,
            forbidSync: forbidSync,
            sendKeyframe: sendKeyframe,
            getFramesByKey: getFramesByKey,
            setSelfInfo: setSelfInfo,
            updateStatus: updateStatus,
        };
    }
    function useKeyframe(key) {
        var live = useLiveInstance();
        var _a = React.useState(live.getFramesByKey(key)), state = _a[0], updateState = _a[1];
        React.useEffect(function () {
            var onUpdate = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return updateState(args[0]);
            };
            live.keyframes.on(key, onUpdate);
            return function () { return live.keyframes.off(key, onUpdate); };
        }, [live]);
        var setState = React.useCallback(function (frame) { return live.sendKeyframe(key, frame); }, [live]);
        return [state, setState];
    }
    function useLiveEventCallback(name, callback, deps) {
        var live = useLiveInstance();
        var dispose = function () { return live.off(name, callback); };
        var dependencyList = deps ? [live, name].concat(deps) : [live, name];
        React.useEffect(function () {
            live.on(name, callback);
            return dispose;
        }, dependencyList);
        return dispose;
    }
    function useReadyState() {
        var live = useLiveInstance();
        var _a = React.useState(live.readyState), state = _a[0], updateState = _a[1];
        React.useEffect(function () {
            var readyStateUpdate = function (readyState) { return updateState(readyState); };
            live.on('ws.readyStateUpdate', readyStateUpdate);
            return function () { return live.off('ws.readyStateUpdate', readyStateUpdate); };
        }, [live]);
        return state;
    }
    function useRTCAction() {
        var live = useLiveInstance();
        var join = React.useCallback(function (options) { return live.$RTC.join(options); }, [live]);
        var quit = React.useCallback(function () { return live.$RTC.quit(); }, [live]);
        var shock = React.useCallback(function () { return live.$RTC.shock(); }, [live]);
        var toggleMicro = React.useCallback(function (flag) { return live.$RTC.toggleMicro(flag); }, [live]);
        var detectMicro = React.useCallback(function () { return live.$RTC.detectMicro(); }, [live]);
        return { join: join, quit: quit, shock: shock, toggleMicro: toggleMicro, detectMicro: detectMicro };
    }
    function useRTCEventCallback(name, callback, deps) {
        var live = useLiveInstance();
        var dispose = function () { return live.$RTC.off(name, callback); };
        var dependencyList = deps ? [live, name].concat(deps) : [live, name];
        React.useEffect(function () {
            live.$RTC.on(name, callback);
            return dispose;
        }, dependencyList);
        return dispose;
    }
    function useSelfInfo() {
        var _this = this;
        var live = useLiveInstance();
        var _a = React.useState(live.selfInfo || {}), state = _a[0], updateState = _a[1];
        var selfInfoUpdate = function (userInfo) { return updateState(userInfo); };
        React.useEffect(function () {
            live.on('selfInfoUpdate', selfInfoUpdate);
            return function () { return live.off('selfInfoUpdate', selfInfoUpdate); };
        }, [live]);
        var setState = React.useCallback(function (userInfo) { return (0, tslib_1.__awaiter)(_this, void 0, void 0, function () {
            return (0, tslib_1.__generator)(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, live.setSelfInfo(userInfo)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        }); }, [live]);
        return [state, setState];
    }
    function useSnapshot() {
        var live = useLiveInstance();
        var _a = React.useState(live.snapshot), state = _a[0], setState = _a[1];
        React.useEffect(function () {
            var onKeyframes = function (keyframes) { return setState(keyframes); };
            live.on('keyframes', onKeyframes);
            return function () { return live.off('keyframes', onKeyframes); };
        });
        return state;
    }
    function useUserList() {
        var live = useLiveInstance();
        var _a = React.useState(live.userList), state = _a[0], updateState = _a[1];
        var userListUpdate = function (userList) { return updateState(userList); };
        React.useEffect(function () {
            live.on('userListUpdate', userListUpdate);
            return function () { return live.off('userListUpdate', userListUpdate); };
        }, [live]);
        return state;
    }
    function useWebSocketEventCallback(name, callback, deps) {
        var live = useLiveInstance();
        var dispose = function () { return live.off(name, callback); };
        var dependencyList = deps ? [live, name].concat(deps) : [live, name];
        React.useEffect(function () {
            live.on(name, callback);
            return dispose;
        }, dependencyList);
        return dispose;
    }
    return {
        live: live,
        LiveProvider: LiveProvider,
        unsafe__useLiveInstance: unsafe__useLiveInstance,
        unsafe__useRTCInstance: unsafe__useRTCInstance,
        useCurrentWebSocketUrl: useCurrentWebSocketUrl,
        useConnect: useConnect,
        useKeyframe: useKeyframe,
        useKeyframeUpdateCallback: useKeyframeUpdateCallback,
        useLiveAction: useLiveAction,
        useLiveEventCallback: useLiveEventCallback,
        useReadyState: useReadyState,
        useRTCAction: useRTCAction,
        useRTCEventCallback: useRTCEventCallback,
        useSelfInfo: useSelfInfo,
        useSnapshot: useSnapshot,
        useUserList: useUserList,
        useWebSocketEventCallback: useWebSocketEventCallback,
    };
}
exports.createLiveReact = createLiveReact;
//# sourceMappingURL=createLiveReact.js.map